import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip } from 'antd';
import {
  AppstoreOutlined,
  MessageOutlined,
  CloudSyncOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  ApartmentOutlined,
  CloudServerOutlined,
  DollarCircleOutlined,
  LineChartOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useThemeStyles, useTheme } from '../context/ThemeContext';
import * as API from '../services/api';

const NavItem = ({ icon: Icon, label, active, onClick, themeStyles, isLight }) => {
  const inactiveColor = themeStyles.textTertiary || themeStyles.textSecondary;

  if (isLight) {
    return (
      <Tooltip placement="right" title={label} mouseEnterDelay={0.1}>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
          onClick={onClick}
          style={{
            width: 34,
            height: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            cursor: 'pointer',
            position: 'relative',
            transition: 'background 0.2s ease, color 0.2s ease',
            background: active ? 'rgba(20, 20, 31, 0.08)' : 'transparent',
            color: active ? themeStyles.textPrimary : inactiveColor,
            marginBottom: 4,
          }}
          onMouseEnter={(e) => {
            if (!active) {
              e.currentTarget.style.background = 'rgba(20, 20, 31, 0.06)';
              e.currentTarget.style.color = themeStyles.textSecondary;
            }
          }}
          onMouseLeave={(e) => {
            if (!active) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = inactiveColor;
            }
          }}
        >
          {active && (
            <div style={{
              position: 'absolute',
              left: -8,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: 18,
              borderRadius: '0 3px 3px 0',
              background: themeStyles.textPrimary,
            }} />
          )}
          <Icon style={{ fontSize: 18 }} />
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip placement="right" title={label} mouseEnterDelay={0.1}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        onClick={onClick}
        style={{
          width: 34,
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.2s ease',
          background: active
            ? 'linear-gradient(135deg, rgba(148,22,127,0.35) 0%, rgba(21,60,180,0.25) 100%)'
            : 'transparent',
          color: active ? themeStyles.textPrimary : inactiveColor,
          marginBottom: 4,
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'rgba(148, 22, 127, 0.12)';
            e.currentTarget.style.color = themeStyles.textSecondary;
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = inactiveColor;
          }
        }}
      >
        {active && (
          <div style={{
            position: 'absolute',
            left: -8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: 16,
            borderRadius: '0 3px 3px 0',
            background: 'linear-gradient(180deg, #F62E97 0%, #153CB4 100%)',
          }} />
        )}
        <Icon style={{ fontSize: 19 }} />
      </div>
    </Tooltip>
  );
};

const Divider = ({ themeStyles, isLight }) => (
  <div
    style={{
      height: 1,
      background: themeStyles.borderColorLight || themeStyles.borderColor,
      /* Tight: still separates “everyday” nav from admin tools without a large empty band */
      margin: isLight ? '2px 8px' : '3px 8px',
    }}
  />
);

const Sidebar = () => {
  const { theme } = useTheme();
  const themeStyles = useThemeStyles();
  const isLight = theme === 'light';
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isAdmin, token } = useAuth();
  const [hasAssignedProjects, setHasAssignedProjects] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const checkAssignments = async () => {
      // Admin personas can still access management areas even without direct assignments.
      if (!token || isAdmin || isSuperAdmin) {
        setHasAssignedProjects(true);
        return;
      }
      try {
        const response = await API.listRbacProjects(token, null, { signal: controller.signal });
        const data = Array.isArray(response?.data) ? response.data : [];
        if (!cancelled) setHasAssignedProjects(data.length > 0);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
          return;
        }
        // Don't hard-block navigation if this request fails.
        if (!cancelled) setHasAssignedProjects(true);
      }
    };

    checkAssignments();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, isAdmin, isSuperAdmin]);

  const isActive = (path) => {
    const loc = location.pathname;
    if (path === '/home') return loc === '/home' || loc === '/';
    if (path === '/projects') return loc === '/projects' || loc.startsWith('/project/');
    if (path === '/deploy') return loc === '/deploy' || loc.startsWith('/deploy/');
    if (path === '/admin/budgets') {
      return loc.startsWith('/admin/budget') || loc === '/admin/cost';
    }
    if (path === '/chat') return loc === '/chat' || loc.startsWith('/chat/');
    if (path === '/about') return loc === '/about' || loc === '/about/v2';
    return loc.startsWith(path);
  };

  const go = (path) => navigate(path);

  // Build items based on role — only include what the user can actually access
  const userItems = [];
  const adminItems = [];

  // Everyone sees Dashboard
  userItems.push({ icon: AppstoreOutlined, label: 'Dashboard', path: '/home' });

  // Group admins use the ingestion wizard; assigned users chat only.
  if (!isSuperAdmin && isAdmin) {
    userItems.push({ icon: CloudSyncOutlined, label: 'Ingestion Pipeline', path: '/deploy' });
  }
  if (!isSuperAdmin && (hasAssignedProjects || isAdmin)) {
    userItems.push({ icon: MessageOutlined, label: 'Chat', path: '/chat' });
  }

  // Super admin only: user management, categories, and groups
  if (isSuperAdmin) {
    adminItems.push({ icon: TeamOutlined, label: 'Users', path: '/users' });
    adminItems.push({ icon: FolderOutlined, label: 'Categories', path: '/categories' });
    adminItems.push({ icon: ApartmentOutlined, label: 'Groups', path: '/groups' });
  }

  // Both admin and super_admin: budgets and document library
  if (isAdmin) {
    adminItems.push({ icon: DollarCircleOutlined, label: 'Budgets', path: '/admin/budgets' });
    adminItems.push({ icon: CloudServerOutlined, label: 'Document Library', path: '/document-library' });
  }

  // Regular users (and group admins) can track their own usage & requests
  if (!isSuperAdmin && hasAssignedProjects) {
    userItems.push({ icon: LineChartOutlined, label: 'My Usage', path: '/me/usage' });
  }

  // Group admin only (not super_admin): assign users to projects
  if (isAdmin && !isSuperAdmin) {
    adminItems.push({ icon: TeamOutlined, label: 'Project Users', path: '/rbac-projects' });
  }

  return (
    <div style={{
      height: '100%',
      width: 52,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: themeStyles.siderBg,
      paddingTop: 10,
      paddingBottom: 8,
      boxSizing: 'border-box',
    }}>
      {/* Main nav items */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
      }}>
        {userItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            active={isActive(item.path)}
            onClick={() => go(item.path)}
            themeStyles={themeStyles}
            isLight={isLight}
          />
        ))}

        {/* Admin section — only render if there are admin items */}
        {adminItems.length > 0 && (
          <>
            {/* Hairline between everyday nav (dashboard / chat / …) and admin-only entries */}
            <Divider themeStyles={themeStyles} isLight={isLight} />
            {adminItems.map((item) => (
              <NavItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                active={isActive(item.path)}
                onClick={() => go(item.path)}
                themeStyles={themeStyles}
                isLight={isLight}
              />
            ))}
          </>
        )}
      </div>

      {/* Bottom section — always visible */}
      {/* About — separated from primary nav */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Divider themeStyles={themeStyles} isLight={isLight} />
        <NavItem icon={InfoCircleOutlined} label="About" active={isActive('/about')} onClick={() => go('/about/v2')} themeStyles={themeStyles} isLight={isLight} />
      </div>
    </div>
  );
};

export default Sidebar;
